/*
 * Copyright (c) 2000-2022 TeamDev. All rights reserved.
 * TeamDev PROPRIETARY and CONFIDENTIAL.
 * Use is subject to license terms.
 */

/**
 * An immutable object that represents a filter's state, which is basically used as
 * a filter's model.
 *
 * The filter state itself is stored in the `criterion` field, and the purpose of having
 * this additional `FilterValue` class on top of `FilterCriterion` is having a `filterId` field,
 * which is essential for implementing composite filters.
 *
 * It is expected that the `FilterValue` class wont' need to be subclassed `FilterCriterion` would
 * have a subclass for each type of filter.
 */
export class FilterValue<I, C extends FilterCriterion<I>> {

  /**
   * A unique ID of the filter where this value comes from.
   */
  readonly filterId: FilterId;

  /**
   * Contains filtering parameter(s) and allows accepting or rejecting items accordingly.
   *
   * A value of `null` means that the filter accepts all items.
   */
  readonly criterion: C | null;

  constructor(filterId: FilterId, criterion: C | null) {
    this.filterId = filterId;
    this.criterion = criterion;
  }

  /**
   * @return `true` if this filter value has a criterion that is known to accept any items that are
   *         given to it
   */
  get acceptsAnyItem(): boolean {
    return (
        this.criterion == null ||
        (this.criterion instanceof CompositeFilterCriterion && this.criterion.acceptsAnyItem)
    );
  }
}

/**
 * A type for unique filter identifiers.
 */
export type FilterId = string;

/**
 * An abstract base class for objects that represent filter criteria. A filter criterion is
 * an object that identifies the state of a filter widget, and allows accepting/rejecting
 * items accordingly.
 *
 * By convention, implementations of this class have to be immutable.
 */
export abstract class FilterCriterion<I> {

  /**
   * Returns `true`, if this filter instance accepts the specified item, and `false` otherwise.
   */
  abstract accepts(item: I): boolean;
}

/**
 * A type of `FilterCriterion` for composite filters. It contains nested `FilterValue` objects, and
 * accepts an item only if all of the nested filters accept it.
 */
export class CompositeFilterCriterion<I> extends FilterCriterion<I> {

  /**
   * Filter values that this filter criterion delegate the item acceptance logic.
   */
  readonly nestedFilterValues: FilterValue<I, FilterCriterion<I>>[];

  constructor(nestedFilterValues: FilterValue<I, FilterCriterion<I>>[]) {
    super();
    this.nestedFilterValues = nestedFilterValues;
  }

  /**
   * @param item  an item to check
   * @return `true`, if all of the nested filters accept the given item, and `false` if at least one
   *          of them doesn't accept it. In case of no nested filters, the item is accepted
   *          (as there's no filter to object its acceptance).
   */
  accepts(item: I): boolean {
    return this.nestedFilterValues.every(f =>
        f.criterion == null || f.criterion.accepts(item)
    );
  }

  /**
   * @return `true` if this composite criterion accepts any items that are given to it, due to the
   *         respective states of the nested filter criteria
   */
  get acceptsAnyItem(): boolean {
    return this.nestedFilterValues.every(fv => fv.acceptsAnyItem);
  }

  /**
   * Searches the subtree of `FilterValue` objects (descending into any `CompositeFilterCriterion`s
   * encountered for a `FilterValue` object having the specified `filterId` value.
   *
   * @param filterId  a filter ID of a `FilterValue` that should be found
   * @return value   a `FilterValue` instance whose `filterId` equals the passed `filterId`
   *                 parameter found in a subtree of filter values, or `undefined` if no such
   *                 `FilterValue` object was found.
   */
  getNestedFilterValue(filterId: FilterId): FilterValue<I, FilterCriterion<I>> | undefined {
    for (const fv of this.nestedFilterValues) {
      if (fv.filterId === filterId) {
        return fv;
      }
      if (fv.criterion instanceof CompositeFilterCriterion) {
        const filterValue = fv.criterion.getNestedFilterValue(filterId);
        if (filterValue) {
          return filterValue;
        }
      }
    }

    return undefined;
  }

  /**
   * An analog of a nested filter value "setter", but returns an new updated instance instead, since
   * `FilterCriterion` objects are immutable.
   *
   * @param newFilterValue  a filter value that should replace the respective nested one with
   *                        the same `filterId` value, or if here's no such nested filter value,
   *                        should just be added to the list of nested filter values
   *
   * @return a new `CompositeFilterCriterion` instance (or its subclass) that has the same nested
   *         filter values as this instance, but one of them that matches the `filterId` of the
   *         `newFilterValue` instance updated. If there's no preexisting `FilterValue` that matches
   *         this `filterId` value, then `newFilterValue` is just added to the existing list of
   *         nested `FilterValue` instances.
   */
  updateNestedFilterValue(
      newFilterValue: FilterValue<I, FilterCriterion<I>>
  ): CompositeFilterCriterion<I> {

    let criterionReplaced = false;
    const updatedFilterValues = this.nestedFilterValues.map(fv => {
      if (fv.filterId !== newFilterValue.filterId) {
        return fv;
      } else {
        if (criterionReplaced) {
          throw (
              'CompositeFilterCriterion is expected to contain no more than one nested filter ' +
              'with the same filterId value: ' + fv.filterId
          );
        }
        criterionReplaced = true;
        return newFilterValue;
      }
    });

    if (!criterionReplaced) {
      updatedFilterValues.push(newFilterValue);
    }

    return this.newInstance(updatedFilterValues);
  }

  /**
   * Creates an instance of the same type as this class one.
   *
   * Has to be overridden in subclasses to instantiate an instance of the respective subclass.
   *
   * @param nestedFilterValues  filter values for a new composite filter criterion instance
   */
  protected newInstance(
      nestedFilterValues: FilterValue<I, FilterCriterion<I>>[]
  ): CompositeFilterCriterion<I> {
    return new CompositeFilterCriterion(nestedFilterValues);
  }
}
